import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";

/**
 * Maintenance mode on the church's website (ADR 0079): the state as `website_offline` gives it. `siteUrl` is where
 * the website lives (null until it has a live address); `website` is whether the church's plan has a website at all.
 */
export interface WebsiteOffline {
  readonly enabled: boolean;
  readonly message: string;
  /** ISO instant, or null. */
  readonly backAt: string | null;
  readonly bypassRotatedAt: string | null;
  readonly setAt: string | null;
  readonly siteUrl: string | null;
  readonly timeZone: string;
  readonly website: boolean;
}

/** What `rotate_website_bypass` answers: the state, and the new link, shown once. */
export interface RotatedBypass extends WebsiteOffline {
  readonly secret: string;
  readonly bypassPath: string;
  readonly bypassUrl: string | null;
}

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);

export function toWebsiteOffline(raw: unknown): WebsiteOffline {
  const d = (raw ?? {}) as Record<string, unknown>;
  return {
    enabled: d["enabled"] === true,
    message: str(d["message"]) ?? "",
    backAt: str(d["backAt"]),
    bypassRotatedAt: str(d["bypassRotatedAt"]),
    setAt: str(d["setAt"]),
    siteUrl: str(d["siteUrl"]),
    timeZone: str(d["timeZone"]) ?? "UTC",
    website: d["website"] === true,
  };
}

export function toRotatedBypass(raw: unknown): RotatedBypass {
  const d = (raw ?? {}) as Record<string, unknown>;
  const secret = str(d["secret"]) ?? "";
  return {
    ...toWebsiteOffline(raw),
    secret,
    bypassPath: str(d["bypassPath"]) ?? `/_how/bypass/${secret}`,
    bypassUrl: str(d["bypassUrl"]),
  };
}

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** Reads the state; needs website.manage (the database refuses others). */
export function useWebsiteOffline(enabled = true) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["website-offline", tenantId],
    enabled: enabled && tenantId !== null,
    queryFn: async (): Promise<WebsiteOffline> => {
      const { data, error } = await supabase.rpc("website_offline", {
        p_tenant_id: tenantId ?? "",
      });
      if (error) throw Object.assign(new Error(error.message), { code: error.code });
      return toWebsiteOffline(data);
    },
  });
}

/** Words for a refusal: the database's own sentence for a broken rule, ours otherwise. */
export function offlineErrorMessage(error: { code?: string; message: string }): string {
  if (error.code === "23514")
    return error.message.charAt(0).toUpperCase() + error.message.slice(1) + ".";
  if (error.code === "42501")
    return "Only someone who manages the website can take it offline or put it back.";
  return "Your website's setting was not saved. Try again.";
}

/** Takes the website offline or puts it back; answers with the state as saved. */
export function useSaveWebsiteOffline() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      enabled: boolean;
      message: string;
      backAt: string | null;
    }): Promise<WebsiteOffline> => {
      const { data, error } = await supabase.rpc("set_website_offline", {
        p_tenant_id: tenantId ?? "",
        p_enabled: input.enabled,
        p_message: input.message,
        ...(input.backAt === null ? {} : { p_back_at: input.backAt }),
      });
      if (error) throw new Error(offlineErrorMessage(error));
      return toWebsiteOffline(data);
    },
    onSuccess: (saved) => {
      client.setQueryData(["website-offline", tenantId], saved);
    },
  });
}

/** A new bypass link; the old one stops. The secret comes back once. */
export function useRotateWebsiteBypass() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<RotatedBypass> => {
      const { data, error } = await supabase.rpc("rotate_website_bypass", {
        p_tenant_id: tenantId ?? "",
      });
      if (error) throw new Error(offlineErrorMessage(error));
      return toRotatedBypass(data);
    },
    onSuccess: (rotated) => {
      client.setQueryData(["website-offline", tenantId], toWebsiteOffline(rotated));
    },
  });
}
